/**
 * «Доступна нова версія» (UX-31; states.md, розд. 5). Service worker у застосунку ще немає, тож
 * нову збірку видно з документа: вхідний скрипт збірки має хеш у назві (`/assets/index-<хеш>.js`),
 * і будь-яка зміна коду чи стилів першого екрана дає інший хеш. Перевірка — `GET /` без кешу
 * (документ малий, edge віддає його `no-cache`), порівняння зі скриптом, з яким стартувала вкладка.
 *
 * Коли перевіряємо: раз на годину (з розкидом, щоб вкладки не йшли хвилею), при поверненні у
 * вкладку й переході між екранами — не частіше ніж раз на годину; одразу — коли не завантажився
 * лінивий чанк (`vite:preloadError`: реліз прибрав файли старої збірки).
 *
 * Автоматичного перезавантаження немає: людина може бути посеред запису. «Оновити» чекає змін у
 * дорозі (до 10 с), далі перезавантажує сторінку; чернетки форм записуються самі на `pagehide`
 * (`useDraft`), тож після перезавантаження — той самий маршрут і «Продовжити запис?».
 * «Пізніше» ховає банер на годину.
 *
 * У dev (Vite без збірки) і в тестах хешованого скрипта немає — перевірка вимкнена.
 */
import { useSyncExternalStore } from 'react';
import { waitForWrites } from './api.ts';

export const CHECK_EVERY_MS = 60 * 60_000;
export const SNOOZE_MS = 60 * 60_000;
/** Після збою чанка — не частіше за це: інакше кожен зламаний перехід ішов би на сервер. */
const FORCED_MIN_MS = 60_000;
/** Скільки «Оновити» чекає змін у дорозі. */
export const WRITES_WAIT_MS = 10_000;

/** Шлях вхідного скрипта збірки (`/assets/index-<хеш>.js`) з HTML документа. */
export function entryScriptOf(html: string): string | null {
  for (const [tag] of html.matchAll(/<script\b[^>]*>/gi)) {
    if (!/\btype=["']?module\b/i.test(tag)) continue;
    const src = /\bsrc=["']([^"']+)["']/i.exec(tag)?.[1];
    if (src && pathOf(src).startsWith('/assets/')) return pathOf(src);
  }
  return null;
}

function pathOf(src: string): string {
  try {
    return new URL(src, location.origin).pathname;
  } catch {
    return src;
  }
}

/** Скрипт, з яким стартувала вкладка; `null` — не збірка (dev, тести). */
function bootEntry(): string | null {
  if (typeof document === 'undefined') return null;
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const path = pathOf(script.src);
    if (path.startsWith('/assets/')) return path;
  }
  return null;
}

interface VersionState {
  available: boolean;
  snoozedUntil: number;
  updating: boolean;
}

let state: VersionState = { available: false, snoozedUntil: 0, updating: false };
let boot: string | null = null;
let lastCheck = 0;
/** Окремо від `lastCheck`: збій чанка одразу після старту теж має перевірити версію. */
let lastForced = 0;
let checking: Promise<void> | null = null;
let snoozeTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();
/** Перезавантаження; тести підміняють. */
let reload = () => location.reload();

function set(next: Partial<VersionState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Банер показуємо: є нова збірка і людина не відклала оновлення. */
export function useNewVersion(): { visible: boolean; updating: boolean } {
  const s = useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
  return { visible: s.available && Date.now() >= s.snoozedUntil, updating: s.updating };
}

/**
 * Перевірити, чи вийшла нова збірка. `force` — після збою чанка (не частіше раз на хвилину), інакше
 * не частіше раз на годину. Збій мережі чи сервера — тиха відмова: наступна перевірка за графіком.
 */
export function checkVersion(force = false): Promise<void> {
  if (!boot || state.available) return Promise.resolve();
  const now = Date.now();
  if (force ? now - lastForced < FORCED_MIN_MS : now - lastCheck < CHECK_EVERY_MS) {
    return Promise.resolve();
  }
  if (force) lastForced = now;
  lastCheck = now;
  checking ??= (async () => {
    try {
      const res = await fetch('/', {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { accept: 'text/html' },
      });
      if (!res.ok) return;
      const entry = entryScriptOf(await res.text());
      if (entry && entry !== boot) set({ available: true });
    } catch {
      // Мережа: стан «немає зв'язку» покаже свій банер, а версію перевіримо пізніше.
    } finally {
      checking = null;
    }
  })();
  return checking;
}

/** «Пізніше»: банер зникає на годину, потім з'являється знову, якщо оновлення так і не було. */
export function snoozeVersion(): void {
  clearTimeout(snoozeTimer);
  set({ snoozedUntil: Date.now() + SNOOZE_MS });
  snoozeTimer = setTimeout(() => set({}), SNOOZE_MS);
}

/** «Оновити»: дочекатися змін у дорозі, тоді перезавантажити (чернетки пишуться на `pagehide`). */
export async function applyVersion(): Promise<void> {
  if (state.updating) return;
  set({ updating: true });
  await waitForWrites(WRITES_WAIT_MS);
  reload();
}

/** Старт застосунку (`main.tsx`). */
export function watchVersion(): () => void {
  boot = bootEntry();
  if (!boot) return () => undefined;
  lastCheck = Date.now();
  // Розкид ±10 %: вкладки, відкриті разом, не питають сервер в одну секунду.
  const every = CHECK_EVERY_MS * (0.9 + Math.random() * 0.2);
  const interval = setInterval(() => void checkVersion(), every);
  const onVisible = () => {
    if (document.visibilityState === 'visible') void checkVersion();
  };
  const onChunkError = () => void checkVersion(true);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('vite:preloadError', onChunkError);
  return () => {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('vite:preloadError', onChunkError);
  };
}

/** Лише для тестів: стан, скрипт старту і перезавантаження. */
export function resetVersionForTests(
  options: { boot?: string | null; reload?: () => void; lastCheck?: number } = {},
): void {
  clearTimeout(snoozeTimer);
  state = { available: false, snoozedUntil: 0, updating: false };
  boot = options.boot ?? null;
  lastCheck = options.lastCheck ?? 0;
  lastForced = options.lastCheck ?? 0;
  checking = null;
  reload = options.reload ?? (() => location.reload());
  for (const listener of listeners) listener();
}
